import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(Draggable, InertiaPlugin);

function ReviewAvatarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 28 28'
      fill='none'
      className='demo-card__avatar-icon'
      aria-hidden='true'
    >
      <path
        d='M13.9987 13.4168C16.576 13.4168 18.6654 11.3275 18.6654 8.75016C18.6654 6.17283 16.576 4.0835 13.9987 4.0835C11.4214 4.0835 9.33203 6.17283 9.33203 8.75016C9.33203 11.3275 11.4214 13.4168 13.9987 13.4168Z'
        stroke='currentColor'
        strokeWidth='1.5'
      ></path>
      <path
        d='M5.25 24.5002V20.6619C5.25 20.1315 5.46071 19.6228 5.83579 19.2477L7.87597 17.2075C8.41163 16.6719 9.20397 16.4848 9.92264 16.7244L13.3675 17.8727C13.7781 18.0095 14.2219 18.0095 14.6325 17.8727L18.0774 16.7244C18.796 16.4848 19.5884 16.6719 20.124 17.2075L22.1642 19.2477C22.5393 19.6228 22.75 20.1315 22.75 20.6619V24.5002'
        stroke='currentColor'
        strokeWidth='1.5'
      ></path>
    </svg>
  );
}

function OverlappingReviewSlider({ reviews }) {
  const sliderRef = useRef(null);

  useGSAP(
    () => {
      const init = sliderRef.current;

      if (!init) {
        return undefined;
      }

      const minScale = +(init.getAttribute('data-scale') ?? 0.45);
      const maxRotation = +(init.getAttribute('data-rotate') ?? -8);
      const wrap = init.querySelector('[data-overlap-slider-collection]');
      const slider = init.querySelector('[data-overlap-slider-list]');
      const slides = Array.from(
        init.querySelectorAll('[data-overlap-slider-item]')
      );

      if (!wrap || !slider || !slides.length) {
        return undefined;
      }

      wrap.style.touchAction = 'none';
      wrap.style.userSelect = 'none';

      let spacing = 0;
      let maxDrag = 0;
      let dragX = 0;
      let draggable;
      let active = false;
      let currentIndex = 0;
      let resizeRafId = 0;

      const clamp = (value) => {
        if (maxDrag <= 0) {
          return 0;
        }

        return Math.min(Math.max(value, 0), maxDrag);
      };

      const setAriaLabel = (idx) => {
        wrap.setAttribute(
          'aria-label',
          `Review highlight slider. Slide ${idx + 1} of ${slides.length}`
        );
      };

      const updateCurrentIndex = () => {
        currentIndex = spacing > 0 ? Math.round(dragX / spacing) : 0;
        setAriaLabel(currentIndex);
      };

      const update = () => {
        gsap.set(slider, { x: -dragX });

        slides.forEach((slide, index) => {
          const threshold = index * spacing;
          const local = Math.max(0, dragX - threshold);
          const progress = spacing > 0 ? Math.min(local / spacing, 1) : 0;

          gsap.set(slide, {
            x: local,
            scale: 1 - (1 - minScale) * progress,
            rotation: maxRotation * progress,
            transformOrigin: '75% center',
          });
        });
      };

      const recalc = () => {
        const style = getComputedStyle(slides[0]);
        const gapRight = parseFloat(style.marginRight) || 0;
        const slideWidth = slides[0].offsetWidth;

        spacing = slideWidth + gapRight;
        maxDrag = spacing * (slides.length - 1);
        dragX = clamp(dragX);
        update();
        updateCurrentIndex();

        if (draggable) {
          draggable.applyBounds({ minX: -maxDrag, maxX: 0 });
        }
      };

      const goToSlide = (idx) => {
        const nextIndex = Math.max(0, Math.min(idx, slides.length - 1));
        const targetX = nextIndex * spacing;

        currentIndex = nextIndex;

        gsap.to({ value: dragX }, {
          value: targetX,
          duration: 0.35,
          ease: 'power4.out',
          onUpdate() {
            dragX = this.targets()[0].value;
            update();
          },
          onComplete() {
            updateCurrentIndex();
          },
        });
      };

      draggable = Draggable.create(slider, {
        type: 'x',
        bounds: { minX: -maxDrag, maxX: 0 },
        inertia: true,
        maxDuration: 1,
        snap(raw) {
          const distance = clamp(-raw);
          const idx = spacing > 0 ? Math.round(distance / spacing) : 0;

          return -idx * spacing;
        },
        onDrag() {
          dragX = clamp(-this.x);
          update();
          updateCurrentIndex();
        },
        onDragEnd() {
          updateCurrentIndex();
        },
        onThrowUpdate() {
          dragX = clamp(-this.x);
          update();
          updateCurrentIndex();
        },
        onThrowComplete() {
          updateCurrentIndex();
        },
      })[0];

      const scheduleRecalc = () => {
        if (resizeRafId) {
          window.cancelAnimationFrame(resizeRafId);
        }

        resizeRafId = window.requestAnimationFrame(() => {
          recalc();
        });
      };

      const resizeObserver = new ResizeObserver(() => {
        scheduleRecalc();
      });

      resizeObserver.observe(init);

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          active = entries[0].isIntersecting;
        },
        { threshold: 0.25 }
      );

      intersectionObserver.observe(init);

      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-roledescription', 'carousel');
      wrap.setAttribute('tabindex', '0');

      const onKey = (event) => {
        if (!active) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goToSlide(currentIndex - 1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goToSlide(currentIndex + 1);
        }
      };

      window.addEventListener('keydown', onKey);

      recalc();

      return () => {
        if (resizeRafId) {
          window.cancelAnimationFrame(resizeRafId);
        }
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        window.removeEventListener('keydown', onKey);
        gsap.killTweensOf(slider);
        draggable?.kill();
      };
    },
    { scope: sliderRef, dependencies: [reviews] }
  );

  return (
    <div
      ref={sliderRef}
      data-overlap-slider-init=''
      data-scale='0.58'
      data-rotate='-6'
      className='overlapping-slider__wrap'
    >
      <div
        data-overlap-slider-collection=''
        className='overlapping-slider__collection'
      >
        <div
          data-overlap-slider-list=''
          className='overlapping-slider__list'
        >
          {reviews.map((review) => (
            <div
              key={review.source}
              data-overlap-slider-item=''
              className='overlapping-slider__item'
            >
              <article className='demo-card'>
                <blockquote className='demo-card__quote'>
                  <span className='demo-card__quote-mark' aria-hidden='true'>
                    “
                  </span>
                  <p className='demo-card__quote-text'>{review.quote}</p>
                  <span className='demo-card__quote-mark' aria-hidden='true'>
                    ”
                  </span>
                </blockquote>
                <div className='demo-card__bottom'>
                  <div className='demo__card-avatar'>
                    <ReviewAvatarIcon />
                  </div>
                  <div className='demo-card__meta'>
                    <span className='demo-card__p'>{review.source}</span>
                    <span className='demo-card__kicker'>{review.type}</span>
                  </div>
                </div>
                <a
                  className='demo-card__link'
                  href={review.href}
                  target='_blank'
                  rel='noreferrer'
                >
                  {review.linkLabel}
                </a>
              </article>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OverlappingReviewSlider;
